import { action, asyncAction } from '@play-co/replicant';
import createActions from 'src/replicant/config/actions/createActions';
import { grantMatch2Rewards } from '../match2/match2.modifiers';
import { getPlayerProfile } from '../profile/profile.getters';
import {
  buddiesAddPlayerToParty,
  buddiesAddInviteSent,
  buddiesRemoveInvite,
  buddiesEventSeenAt,
  buddiesClearLastScoreAdded,
  buddiesMarkFTUESeen,
  buddiesClaimEventRewards,
  buddiesRejectSuggestion as buddiesRejectSuggestionModifier,
  buddiesRecordNudge,
  buddiesAcknowledgePartyJoins,
} from './buddies.modifiers';
import {
  buddiesCanNudgePlayer,
  buddiesCanSendOrReceiveInvite,
  buddiesGetEvent,
  buddiesIsEventCompleted,
  buddiesIsPartyFull,
} from './buddies.getters';
import { buddiesPartyJoinRewards, buddiesSchedule } from './buddies.ruleset';

export type BuddiesInviteResult = {
  success: boolean;
  reason?:
    | 'senderPartyIsFull'
    | 'receiverPartyIsFull'
    | 'userNotFound'
    | 'eventCompleted';
};

export const buddiesActions = createActions({
  buddiesSendInvite: action((state, args: { receiverId: string }, api) => {
    if (!buddiesCanSendOrReceiveInvite(state, api.date.now())) return;

    buddiesAddInviteSent(state, {
      id: args.receiverId,
      now: api.date.now(),
    });
    api.postMessage.buddiesAddInviteReceived(args.receiverId, {});

    api.scheduledActions.schedule.sendInviteBuddyOA({
      args: {},
      notificationId: `buddiesSendInvite`,
      delayInMS: 0,
      targetUserId: args.receiverId,
    });
  }),

  buddiesRemoveInvite: action((state, args: { id: string }, api) => {
    buddiesRemoveInvite(state, {
      id: args.id,
      now: api.date.now(),
    });
    api.postMessage.buddiesRemoveInvite(args.id, {});
  }),

  buddiesAcceptInvite: asyncAction(
    async (
      state,
      args: { senderId: string },
      api,
    ): Promise<BuddiesInviteResult> => {
      // Reject if event is completed (grand prize reached)
      if (buddiesIsEventCompleted(state, api.date.now())) {
        buddiesRemoveInvite(state, { id: args.senderId, now: api.date.now() });
        return { success: false, reason: 'eventCompleted' };
      }

      // First, we have to verify the sender is still available and has space in their party
      const statesMap = await api.fetchStates([args.senderId]);
      const senderState = statesMap[args.senderId]?.state;

      // Remove the invite if the sender is not available
      if (!senderState) {
        buddiesRemoveInvite(state, { id: args.senderId, now: api.date.now() });
        return { success: false, reason: 'userNotFound' };
      }

      // Check if sender's party is full, and just remove invite if it is
      const isTheirPartyFull = buddiesIsPartyFull(senderState, api.date.now());
      if (isTheirPartyFull) {
        buddiesRemoveInvite(state, { id: args.senderId, now: api.date.now() });
        return { success: false, reason: 'senderPartyIsFull' };
      }

      // Reject if receiver's own party is full
      if (buddiesIsPartyFull(state, api.date.now())) {
        buddiesRemoveInvite(state, { id: args.senderId, now: api.date.now() });
        return { success: false, reason: 'receiverPartyIsFull' };
      }

      // Add sender to the main player's party
      buddiesAddPlayerToParty(state, {
        id: args.senderId,
        now: api.date.now(),
      });
      api.postMessage.buddiesAddPlayerToParty(args.senderId, {});

      // Cleanup invites sent if party is full
      if (buddiesIsPartyFull(state, api.date.now())) {
        const event = buddiesGetEvent(state, api.date.now());
        if (!event) throw new Error('No event available after accepted invite');
        for (const id in event.invites) {
          api.postMessage.buddiesRemoveInvite(id, {});
        }
        event.invites = {};
      }

      return { success: true };
    },
  ),

  buddiesUpdateEventSeenAt: action((state, args: void, api) => {
    buddiesEventSeenAt(state, api.date.now());
  }),

  buddiesClearAddedScores: action((state, args: void, api) => {
    buddiesClearLastScoreAdded(state, api.date.now());
  }),

  buddiesMarkFTUESeen: action((state, _: void, api) => {
    buddiesMarkFTUESeen(state, api.date.now());
  }),

  buddiesRejectSuggestion: action((state, args: { playerId: string }, api) => {
    buddiesRejectSuggestionModifier(state, {
      playerId: args.playerId,
      now: api.date.now(),
    });
  }),

  buddiesClaimPartyJoinRewards: action((state, _: void, api) => {
    const newJoins = buddiesAcknowledgePartyJoins(state, api.date.now());
    for (let i = 0; i < newJoins; i++) {
      grantMatch2Rewards(
        state,
        buddiesPartyJoinRewards,
        { feature: 'baking_buddies', $subFeature: 'buddies_party_join' },
        api,
      );
    }
  }),

  buddiesClaimEventReward: action((state, args: { eventId: string }, api) => {
    const gems = buddiesClaimEventRewards(state, args.eventId, api.date.now());
    if (!gems) return;

    grantMatch2Rewards(
      state,
      [{ type: 'gems', amount: gems }],
      { feature: 'baking_buddies', $subFeature: 'buddies_event_reward' },
      api,
    );
  }),

  buddiesSendNudge: action((state, args: { recipientId: string }, api) => {
    const now = api.date.now();

    // Ensure there is a current event
    const schedule = buddiesSchedule.getCurrentEvent(now);
    if (!schedule) return;

    // Validate using existing getter (checks party membership, cooldown, max count)
    if (!buddiesCanNudgePlayer(state, args.recipientId, now)) return;

    // Record the nudge in state
    buddiesRecordNudge(state, { recipientId: args.recipientId, now });

    // Get sender profile for the chatbot message
    const profile = getPlayerProfile(
      state,
      state.publicProfile?.name,
      state.publicProfile?.photo,
    );

    // Schedule delivery on the recipient's state
    api.scheduledActions.schedule.sendBuddiesNudge({
      args: {
        senderId: state.id,
        senderName: profile.name,
        senderPhoto: profile.photo,
      },
      delayInMS: 0,
      targetUserId: args.recipientId,
      notificationId: `buddiesNudge_${schedule.id}_${state.id}_${args.recipientId}`,
    });
  }),
});
